'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { withLayout } from '../../shared/Styles.js'
import { Text, Picture, Heading, Container } from '../../components/index.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import dayjs from 'dayjs'
import { cva } from 'class-variance-authority'
import type { Media } from '../../types/props.js'
export type EventScheduleSemanticName = 'root' | 'timeline' | 'eventScheduleCard'

/**
 * 时间状态类型
 */
export type TimeStatus = 'completed' | 'in-progress' | 'not-started'

/**
 * 状态化图标配置
 */
export interface StateIcon {
  /** 激活状态的图标（进行中） */
  active: Media
  /** 非激活状态的图标（未开始） */
  inactive: Media
  /** 已完成状态的图标（可选，不提供则使用 inactive） */
  completed?: Media
}

/**
 * 活动日程项数据接口
 */
export interface EventScheduleItem {
  /** 标题 */
  title: string
  /** 开始日期（ISO 8601 格式或任何 dayjs 支持的格式，如 '2024-12-01'） */
  startDate: string
  /** 结束日期（ISO 8601 格式或任何 dayjs 支持的格式，如 '2024-12-31'） */
  endDate: string
  /**
   * 卡片背景图标配置，状态化图标（必须包含 active 和 inactive）
   *
   * @example
   * icon: {
   *   active: { url: "https://example.com/active-icon.svg", alt: "Active Icon" },
   *   inactive: { url: "https://example.com/inactive-icon.svg", alt: "Inactive Icon" }
   * }
   */
  icon?: StateIcon
  /** 详细信息列表 */
  items: {
    /** 图标 (SVG 字符串或 URL) */
    icon?: string
    /** 文本内容 */
    label: string
  }[]
}

/**
 * EventSchedule 业务组件数据接口
 */
export interface EventScheduleData {
  /** 日程列表 */
  scheduleList: EventScheduleItem[]
  /** 是否显示时间轴，默认为 true */
  showTimeline?: boolean
  /** 主题模式 */
  theme?: 'light' | 'dark'
}

export interface EventScheduleProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: EventScheduleData
  classNames?: Partial<Record<EventScheduleSemanticName, string>>
}

/**
 * 获取时间状态
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @returns 时间状态：completed（已完成）、in-progress（进行中）、not-started（未开始）
 */
const getTimeStatus = (startDate: string, endDate: string): TimeStatus => {
  const now = dayjs()
  const start = dayjs(startDate).startOf('day')
  const end = dayjs(endDate).endOf('day')

  // 当前时间在结束时间之后 - 已完成
  if (now.isAfter(end)) {
    return 'completed'
  }

  // 当前时间在开始时间之前 - 未开始
  if (now.isBefore(start)) {
    return 'not-started'
  }

  // 当前时间在开始和结束之间 - 进行中
  return 'in-progress'
}

/**
 * 时间轴进度条样式变体（支持三种时间状态）
 */
const timelineProgressActiveVariants = cva('h-1 w-full overflow-hidden', {
  variants: {
    state: {
      // 进行中
      'in-progress-light': 'bg-[#F6CD4E]',
      'in-progress-dark': 'bg-[#D79941]',
      // 未开始
      'not-started-light': 'bg-[#EAEAEC]',
      'not-started-dark': 'bg-[#1E2024]',
      // 已完成
      'completed-light': 'bg-[#F6CD4E]',
      'completed-dark': 'bg-[#D79941]',
    },
  },
  defaultVariants: {
    state: 'not-started-light',
  },
})

/**
 * 时间轴节点样式变体（支持三种时间状态）
 */
const timelineNodeActiveVariants = cva('size-4 rounded-full transition-colors', {
  variants: {
    state: {
      // 进行中
      'in-progress-light': 'bg-[#F6CD4E]',
      'in-progress-dark': 'bg-[#D79941]',
      // 未开始
      'not-started-light': 'bg-[#EAEAEC]',
      'not-started-dark': 'bg-[#1E2024]',
      // 已完成（与进行中颜色一致）
      'completed-light': 'bg-[#F6CD4E]',
      'completed-dark': 'bg-[#D79941]',
    },
  },
  defaultVariants: {
    state: 'not-started-light',
  },
})

/**
 * 卡片文本样式变体（支持三种时间状态）
 */
const cardTextVariants = cva('font-bold leading-[1.2]', {
  variants: {
    state: {
      // 进行中（保持原有颜色）
      'in-progress-light': 'text-[#080A0F]',
      'in-progress-dark': 'text-white',
      // 未开始（60% 透明度）
      'not-started-light': 'text-[#080A0F]',
      'not-started-dark': 'text-[#F5F6F7]',
      // 已完成（60% 透明度）
      'completed-light': 'text-[#4A4C56]/60',
      'completed-dark': 'text-[#F5F6F7]/60',
    },
  },
  defaultVariants: {
    state: 'not-started-light',
  },
})

/**
 * 卡片容器样式变体（支持三种时间状态）
 */
const eventScheduleCardVariants = cva(
  'laptop:h-[160px] rounded-box relative flex h-[120px] min-w-0 flex-col justify-between gap-2 overflow-hidden',
  {
    variants: {
      state: {
        // 进行中
        'in-progress-light': 'bg-[#F4E8BC]',
        'in-progress-dark': 'bg-[#D79941]',
        // 未开始
        'not-started-light': 'bg-[#EAEAEC]',
        'not-started-dark': 'bg-[#1E2024]',
        // 已完成
        'completed-light': 'bg-[#F4E8BC]',
        'completed-dark': 'bg-[#D79941]',
      },
    },
    defaultVariants: {
      state: 'not-started-light',
    },
  }
)

/**
 * 时间轴节点组件 Props
 */
interface TimelineNodeProps {
  /** 时间状态 */
  timeStatus: TimeStatus
  /** 主题模式 */
  theme?: 'light' | 'dark'
  /** 自定义类名 */
  className?: string
  /** 当前节点索引 */
  index: number
  /** 节点总数 */
  nodeLength: number
}

/**
 * 时间轴节点组件
 */
const TimelineNode = ({ timeStatus, theme = 'light', className, index, nodeLength }: TimelineNodeProps) => {
  // 计算组合状态
  const state = `${timeStatus}-${theme}` as
    | 'in-progress-light'
    | 'in-progress-dark'
    | 'not-started-light'
    | 'not-started-dark'
    | 'completed-light'
    | 'completed-dark'

  // 判断首尾位置
  const isFirst = index === 0
  const isLast = index === nodeLength - 1

  return (
    <div className="relative my-2 flex h-1 w-full items-center justify-center">
      {/* 进度条背景 */}
      <div
        className={cn(
          timelineProgressActiveVariants({ state }),
          isFirst && 'rounded-l-full',
          isLast && 'rounded-r-full',
          className
        )}
      />
      {/* 时间节点圆点 */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className={timelineNodeActiveVariants({ state })} />
      </div>
    </div>
  )
}

/**
 * 根据时间状态获取对应的图标
 * @param icon 状态化图标配置
 * @param timeStatus 时间状态
 * @returns 图标 Media 对象
 */
const getIconByTimeStatus = (icon: StateIcon | undefined, timeStatus: TimeStatus): Media | undefined => {
  if (!icon) return undefined
  // 进行中使用 active 图标
  if (timeStatus === 'in-progress') return icon.active
  // 已完成使用 completed 图标（如果有），否则使用 inactive
  if (timeStatus === 'completed') return icon.completed || icon.inactive
  // 未开始使用 inactive 图标
  return icon.inactive
}

/**
 * 活动日程卡片组件
 */
const EventScheduleCard = ({
  timeStatus,
  item,
  theme = 'light',
  className,
  scheduleCount,
}: {
  timeStatus: TimeStatus
  item: EventScheduleItem
  theme?: 'light' | 'dark'
  className?: string
  scheduleCount: number
}) => {
  const currentIcon = React.useMemo(() => {
    return getIconByTimeStatus(item.icon, timeStatus)
  }, [item.icon, timeStatus])

  // 计算组合状态
  const cardState = `${timeStatus}-${theme}` as
    | 'in-progress-light'
    | 'in-progress-dark'
    | 'not-started-light'
    | 'not-started-dark'
    | 'completed-light'
    | 'completed-dark'

  return (
    <div className={cn(eventScheduleCardVariants({ state: cardState }), className)}>
      {/* 背景图标 */}
      {currentIcon && (
        <div className="laptop:w-[128px] desktop:w-[160px] absolute bottom-0 right-0 z-10 w-[120px]">
          <Picture source={currentIcon.url} alt={currentIcon.alt} className="aspect-square" />
        </div>
      )}
      {/* 内容区域 */}
      <div className="tablet:px-4 tablet:py-3 desktop:pl-6 desktop:pr-16 relative z-20 flex h-full flex-col justify-between p-4 pr-8">
        <Heading
          html={item.title}
          className={cn(
            scheduleCount >= 4 ? 'desktop:text-[24px]' : 'desktop:text-[32px]',
            'laptop:text-[24px] line-clamp-1 text-[20px]',
            cardTextVariants({ state: cardState })
          )}
        />
        {/* 详细信息列表 */}
        <div className="flex flex-col gap-0.5">
          {item.items.map((detail, index) => (
            <div key={index} className="flex items-center gap-2">
              {/* 图标 */}
              {detail.icon && (
                <Text
                  className={cn('desktop:size-6 size-5 shrink-0', cardTextVariants({ state: cardState }))}
                  html={detail.icon}
                />
              )}
              {/* 文本 */}
              <Text
                html={detail.label}
                className={cn(
                  'lg-desktop:text-[18px] desktop:text-[16px] line-clamp-1 flex-1 text-[14px] leading-[1.4]',
                  cardTextVariants({ state: cardState })
                )}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * EventSchedule - 活动日程组件
 *
 * @description 显示活动日程时间轴和活动卡片列表
 */
const EventSchedule = React.forwardRef<HTMLDivElement, EventScheduleProps>(
  ({ classNames = {}, data, className, ...rest }, ref) => {
    const theme = data.theme || 'light'
    const swiperRef = React.useRef<SwiperType | null>(null)
    const timelineSwiperRef = React.useRef<SwiperType | null>(null)
    const isSyncingRef = React.useRef(false) // 防止循环触发
    const itemsPerRow = React.useMemo(() => {
      return data?.scheduleList?.length || 2
    }, [data?.scheduleList])

    // 计算进行中 item 的索引（用于自动滚动）
    const activeIndex = React.useMemo(() => {
      return data.scheduleList.findIndex(item => {
        const timeStatus = getTimeStatus(item.startDate, item.endDate)
        return timeStatus === 'in-progress'
      })
    }, [data.scheduleList])

    /**
     * 生成统一的 Swiper 断点配置
     * @param itemsPerRow 每行显示的项目数
     * @param withSpaceBetween 是否包含间距配置
     * @returns Swiper 断点配置对象
     */
    const generateSwiperBreakpoints = React.useCallback((itemsPerRow: number, withSpaceBetween: boolean = false) => {
      // 根据 itemsPerRow 确定不同断点下的 slidesPerView
      const getBreakpointConfig = (breakpoint: 'mobile' | 'tablet' | 'laptop' | 'desktop') => {
        switch (breakpoint) {
          case 'mobile':
            return { slidesPerView: 1.17 }
          case 'tablet':
            if (itemsPerRow === 2) return { slidesPerView: 2 }
            return { slidesPerView: 2.4 }
          case 'laptop':
            if (itemsPerRow === 2) return {}
            if (itemsPerRow === 4) return { slidesPerView: 3.2 }
            return { slidesPerView: itemsPerRow }
          case 'desktop':
            if (itemsPerRow === 2) return { slidesPerView: 2 }
            return { slidesPerView: itemsPerRow }
        }
      }

      const mobileConfig = getBreakpointConfig('mobile')
      const tabletConfig = getBreakpointConfig('tablet')
      const laptopConfig = getBreakpointConfig('laptop')
      const desktopConfig = getBreakpointConfig('desktop')

      // 如果需要 spaceBetween，则添加到配置中
      if (withSpaceBetween) {
        return {
          0: { ...mobileConfig, spaceBetween: 12 },
          768: { ...tabletConfig, spaceBetween: 12 },
          1024: { ...laptopConfig, spaceBetween: 16 },
          1440: { ...desktopConfig, spaceBetween: 16 },
        }
      }

      return {
        0: mobileConfig,
        768: tabletConfig,
        1024: laptopConfig,
        1440: desktopConfig,
      }
    }, [])

    // 根据 itemsPerRow 配置响应式断点（卡片 Swiper - 包含间距）
    const swiperBreakpoints = React.useMemo(() => {
      return generateSwiperBreakpoints(itemsPerRow, true)
    }, [itemsPerRow, generateSwiperBreakpoints])

    // 时间轴 Swiper 断点配置（不包含间距）
    const timeNodeSwiperBreakpoints = React.useMemo(() => {
      return generateSwiperBreakpoints(itemsPerRow, false)
    }, [itemsPerRow, generateSwiperBreakpoints])

    const showTimeline = data.showTimeline !== false // 默认为 true

    // 同步两个 Swiper 的滑动
    const handleCardSwiperSlideChange = React.useCallback((swiper: SwiperType) => {
      if (isSyncingRef.current || !timelineSwiperRef.current) return
      isSyncingRef.current = true
      timelineSwiperRef.current.slideTo(swiper.activeIndex, swiper.params.speed)
      // 延迟重置标志，确保同步完成
      setTimeout(() => {
        isSyncingRef.current = false
      }, 50)
    }, [])

    const handleTimelineSwiperSlideChange = React.useCallback((swiper: SwiperType) => {
      if (isSyncingRef.current || !swiperRef.current) return
      isSyncingRef.current = true
      swiperRef.current.slideTo(swiper.activeIndex, swiper.params.speed)
      // 延迟重置标志，确保同步完成
      setTimeout(() => {
        isSyncingRef.current = false
      }, 50)
    }, [])

    // 自动滚动到 active item
    React.useEffect(() => {
      if (swiperRef.current && timelineSwiperRef.current && activeIndex >= 0) {
        // 延迟执行，确保 Swiper 已经完全初始化
        setTimeout(() => {
          isSyncingRef.current = true
          swiperRef.current?.slideTo(activeIndex, 500)
          timelineSwiperRef.current?.slideTo(activeIndex, 500)
          setTimeout(() => {
            isSyncingRef.current = false
          }, 600)
        }, 100)
      }
    }, [activeIndex])

    return (
      <Container {...rest} ref={ref} className={cn('overflow-hidden', classNames?.root, className)}>
        {showTimeline && (
          <Swiper
            breakpoints={timeNodeSwiperBreakpoints}
            className="h-4 w-full !overflow-visible"
            onSwiper={swiper => {
              timelineSwiperRef.current = swiper
            }}
            onSlideChange={handleTimelineSwiperSlideChange}
          >
            {data.scheduleList.map((item, index) => {
              const timeStatus = getTimeStatus(item.startDate, item.endDate)
              return (
                <SwiperSlide key={'timelineNode' + index} className="">
                  <TimelineNode
                    className={classNames?.timeline}
                    timeStatus={timeStatus}
                    theme={theme}
                    index={index}
                    nodeLength={data.scheduleList.length}
                  />
                </SwiperSlide>
              )
            })}
          </Swiper>
        )}
        <Swiper
          breakpoints={swiperBreakpoints}
          className="w-full !overflow-visible"
          onSwiper={swiper => {
            swiperRef.current = swiper
          }}
          onSlideChange={handleCardSwiperSlideChange}
        >
          {data.scheduleList.map((item, index) => {
            const timeStatus = getTimeStatus(item.startDate, item.endDate)
            return (
              <SwiperSlide key={'SwiperSlideItem' + index}>
                <EventScheduleCard
                  timeStatus={timeStatus}
                  className={cn(showTimeline ? 'laptop:mt-4 mt-2' : '', classNames?.eventScheduleCard)}
                  item={item}
                  theme={theme}
                  scheduleCount={data.scheduleList.length}
                />
              </SwiperSlide>
            )
          })}
        </Swiper>
      </Container>
    )
  }
)

EventSchedule.displayName = 'EventSchedule'
export default withLayout(EventSchedule)

// 导出类型供外部使用
// export type { StateIcon, EventScheduleItem, EventScheduleData, EventScheduleProps }
